import {
  Box,
  Button,
  Fade,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { clearPendingRedirect } from '@src/reducers/conversationalAgent';
import SparkleIcon from '@src/resources/icons/SparkleIcon';
import selectPendingRedirect from '@src/selectors/conversationalAgent';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const TOAST_DURATION = 15000;
const TICK_INTERVAL = 50;
const WINDOW_FEATURES = 'noopener,noreferrer';

const RedirectSnackbar: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const pendingRedirect = useSelector(selectPendingRedirect);

  const requestId = pendingRedirect?.requestId;

  const [progress, setProgress] = useState(100);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleDismiss = useCallback(() => {
    clearTimer();
    dispatch(clearPendingRedirect());
  }, [dispatch, clearTimer]);

  const handleOpen = useCallback(() => {
    if (pendingRedirect) {
      window.open(pendingRedirect.url, '_blank', WINDOW_FEATURES);
    }
    handleDismiss();
  }, [pendingRedirect, handleDismiss]);

  useEffect(() => {
    if (!requestId) {
      clearTimer();
      setProgress(100);
      return undefined;
    }

    setProgress(100);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / TOAST_DURATION) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        dispatch(clearPendingRedirect());
      }
    }, TICK_INTERVAL);

    return clearTimer;
  }, [requestId, dispatch, clearTimer]);

  // A pending redirect must not outlive the toast: when this component
  // unmounts (e.g. navigating into /agent/chat, where the agent UI is
  // hidden), discard it so a stale toast can't reappear on remount.
  useEffect(
    () => () => {
      dispatch(clearPendingRedirect());
    },
    [dispatch]
  );

  return (
    <Fade in={pendingRedirect !== null} timeout={250}>
      <Paper
        className={classes.root}
        elevation={8}
        role="alert"
        aria-live="assertive"
      >
        <Box className={classes.header}>
          <Box className={classes.labelRow}>
            <Box className={classes.iconCircle}>
              <SparkleIcon
                width={12}
                height={12}
                style={{ color: '#FFFFFF' }}
              />
            </Box>
            <Typography className={classes.label}>
              {formatMessage({ id: 'ConversationalAgent.assistantLabel' })}
            </Typography>
          </Box>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={handleDismiss}
            aria-label={formatMessage({
              id: 'ConversationalAgent.closeBanner',
            })}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box className={classes.body}>
          <Typography className={classes.message}>
            <FormattedMessage
              id="ConversationalAgent.redirectMessage"
              values={{
                highlight: (chunks: React.ReactNode) => (
                  <span className={classes.highlight}>{chunks}</span>
                ),
              }}
            />
          </Typography>
          <Button
            className={classes.openButton}
            variant="contained"
            size="small"
            onClick={handleOpen}
            disableElevation
          >
            {formatMessage({ id: 'ConversationalAgent.redirectOpen' })}
          </Button>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          className={classes.progress}
        />
      </Paper>
    </Fade>
  );
};

export default RedirectSnackbar;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: 'calc(100% + 20px)',
    right: -10,
    width: 300,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: theme.zIndex.tooltip,
    border: `1px solid ${theme.b2chatgray[200]}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px 0',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  iconCircle: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  label: {
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: 600,
  },
  closeButton: {
    color: theme.b2chatgray[400],
    padding: 4,
    '&:hover': {
      color: theme.b2chatgray[700],
      backgroundColor: theme.b2chatgray[100],
    },
  },
  body: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 12px 12px',
  },
  message: {
    flex: 1,
    color: theme.b2chatgray[500],
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  highlight: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  openButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 12,
    padding: '4px 14px',
    borderRadius: 6,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  progress: {
    height: 3,
    backgroundColor: theme.b2chatgray[200],
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));
